/**
 * VisibilityTabContent — the system-web-only editor tab that controls
 * which tenants can see and adopt a system-authored import template.
 *
 * Mounted inside the shared UnifiedImportWizard via its `renderExtraTab`
 * slot (see `UnifiedImportWizardPreviewPage` in system-web). Lives in
 * system-web because template visibility is an inherently cross-tenant
 * concern that tenant-web never needs to surface.
 */

import { useState } from 'react';
import { Globe, Lock, Building2, X, Plus } from 'lucide-react';
import type { ImportTemplateVisibility } from '@firesquirrel/local-db';
import type { TemplateEditorState } from '@firesquirrel/import-editor';

const VISIBILITY_OPTIONS: {
  value: ImportTemplateVisibility;
  label: string;
  desc: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'system-only',
    label: 'System Only',
    desc: 'Visible only to system admins inside system-web.',
    icon: <Lock className="h-4 w-4 text-gray-500" />,
  },
  {
    value: 'all-departments',
    label: 'All Departments',
    desc: 'Available to all department admins as a read-only template.',
    icon: <Globe className="h-4 w-4 text-blue-500" />,
  },
  {
    value: 'specific-tenants',
    label: 'Specific Tenants',
    desc: 'Visible only to the tenant IDs listed below.',
    icon: <Building2 className="h-4 w-4 text-orange-500" />,
  },
];

export interface VisibilityTabContentProps {
  state: TemplateEditorState;
  onChange: (patch: Partial<TemplateEditorState>) => void;
}

export default function VisibilityTabContent({ state, onChange }: VisibilityTabContentProps) {
  const [tenantInput, setTenantInput] = useState('');

  const addTenant = () => {
    const id = tenantInput.trim();
    if (!id) return;
    if ((state.allowedTenantIds ?? []).includes(id)) return;
    onChange({ allowedTenantIds: [...(state.allowedTenantIds ?? []), id] });
    setTenantInput('');
  };

  const removeTenant = (id: string) => {
    onChange({ allowedTenantIds: (state.allowedTenantIds ?? []).filter(t => t !== id) });
  };

  return (
    <div className="space-y-5 max-w-xl">
      <p className="text-xs text-gray-500">
        Controls which tenants can see and use this system template in their import wizard.
      </p>

      {/* Visibility selector */}
      <div className="space-y-2">
        {VISIBILITY_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            data-testid={`visibility-${opt.value}`}
            onClick={() => onChange({ visibility: opt.value })}
            className={[
              'w-full flex items-start gap-3 border-2 rounded-lg px-4 py-3 text-left transition-colors',
              state.visibility === opt.value
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 bg-white',
            ].join(' ')}
          >
            <span className="mt-0.5 shrink-0">{opt.icon}</span>
            <div>
              <div className="text-sm font-semibold text-gray-800">{opt.label}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Specific tenant list */}
      {state.visibility === 'specific-tenants' && (
        <div className="space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-xs font-semibold text-gray-700">Allowed Tenant IDs</div>

          {(state.allowedTenantIds ?? []).length === 0 ? (
            <p className="text-xs text-amber-700">
              No tenants added yet. This template will be visible to nobody until at least one tenant ID is added.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(state.allowedTenantIds ?? []).map(id => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-700 text-xs rounded-full px-3 py-1"
                >
                  {id}
                  <button
                    type="button"
                    onClick={() => removeTenant(id)}
                    className="ml-0.5 text-gray-400 hover:text-red-500"
                    aria-label={`Remove tenant ${id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="text"
              data-testid="tenant-id-input"
              className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Paste tenant ID…"
              value={tenantInput}
              onChange={e => setTenantInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTenant(); } }}
            />
            <button
              type="button"
              onClick={addTenant}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-md"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Validates the visibility tab. Used by UnifiedImportWizard's
 * `extraTabErrors` so the tab surfaces an error badge when the user
 * chose 'specific-tenants' but left the list empty — matches the
 * implicit rule the legacy editor enforces silently on save.
 */
export function validateVisibilityTab(state: TemplateEditorState): string[] {
  if (state.visibility === 'specific-tenants' && (state.allowedTenantIds ?? []).length === 0) {
    return ['Add at least one tenant ID or choose a different visibility option.'];
  }
  return [];
}
